import React, { useState, useEffect, useCallback } from 'react'
import {
  Button,
  message,
  Typography,
  Spin,
  Form,
  Input,
  Select,
  DatePicker,
  TimePicker,
  Space,
  Modal,
  InputNumber,
  Checkbox,
  Dropdown,
  Tag,
  Tooltip
} from 'antd'
import {
  EditOutlined,
  SaveOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  CalculatorOutlined,
  ArrowLeftOutlined,
  DollarOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  DownOutlined,
  EyeOutlined,
  EyeInvisibleOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import api from '../utils/api'
import dayjs from 'dayjs'
// createdAt / updatedAt / depositDueDate 是 Mongo 的絕對時刻（UTC），
// 用 dayjs().format() 直接印會跟著瀏覽器時區跑；一律走 formatTz
import { formatTz } from '../utils/timezone'
import { useAuth } from '../hooks/AuthContext'
import { useNavigate } from 'react-router-dom'
import { useMembership } from '../hooks/useMembership'
import useGoBack from '../hooks/useGoBack'
import ConsentFormBookingSection from './ConsentFormBookingSection'
import ServicePhotoBookingSection from './ServicePhotoBookingSection'
import ServiceSelector from './ServiceSelector'
import { useMemo } from 'react'

const { Text } = Typography
const { Option, OptGroup } = Select

// 全局樣式覆蓋
const GlobalStyles = styled.div`
  .ant-input,
  .ant-select-selector,
  .ant-picker,
  .ant-input-number {
    border-radius: 12px !important;
    border: 1px solid #e5e5e7 !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
    transition: all 0.3s ease !important;

    &:hover {
      border-color: #d5728a !important;
      box-shadow: 0 2px 8px rgba(213, 114, 138, 0.2) !important;
    }

    &:focus,
    &.ant-picker-focused {
      border-color: #d5728a !important;
      box-shadow: 0 0 0 3px rgba(213, 114, 138, 0.1) !important;
    }
  }

  .ant-card {
    border-radius: 20px !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
    border: 1px solid #e5e5e7 !important;
  }

  .ant-modal-content {
    border-radius: 20px !important;
    overflow: hidden !important;
  }

  .ant-modal-header {
    border-bottom: 1px solid #e5e5e7 !important;
    background: #f8f9fa !important;
  }
`

// 現代化容器設計
const PageContainer = styled.div`
  min-height: 100%;
  background: transparent;
  padding: 0;
  margin: 0;
  width: 100%;
  box-sizing: border-box;
`

const HeaderSection = styled.div`
  background: #fff;
  border-bottom: 1px solid #e5e5e7;
  padding: 16px 20px;
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 16px;
  border-radius: 12px;

  @media (max-width: 1024px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 1000;
    padding: 12px 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`

const BackButton = styled(Button)`
  height: 36px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 13px;
  border: 1px solid #e5e5e7;
  background: #fff;
  color: #1d1d1f;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: #d5728a;
    color: #d5728a;
    background: #fff;
  }

  @media (max-width: 768px) {
    height: 40px;
    font-size: 14px;
    padding: 0 16px;
  }
`

const HeaderTitle = styled.div`
  flex: 1;
  font-size: 18px;
  font-weight: 600;
  color: #1d1d1f;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const ContentSection = styled.div`
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 16px 20px 24px;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    padding-top: 80px; /* 為固定的 HeaderSection 留出空間 */
  }

  @media (max-width: 768px) {
    padding: 12px 16px 20px;
    padding-top: 30px; /* 為固定的 HeaderSection 留出空間 */
  }
`

const ModernCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    padding: 16px;
    margin-bottom: 12px;
    border-radius: 8px;
  }
`

const SectionTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 12px;
  }
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 12px;
  }
`

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  transition: all 0.2s ease;
`

const InfoLabel = styled.div`
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

const InfoValue = styled.div`
  font-size: 14px;
  color: #1d1d1f;
  font-weight: 500;
  line-height: 1.3;
`

const CustomerNameLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  color: #b85c75;
  cursor: pointer;
  text-align: left;
  &:hover {
    color: #d5728a;
    text-decoration: underline;
  }
`

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  background: ${(props) => props.color || '#f5f5f7'};
  color: ${(props) => props.textColor || '#1d1d1f'};
  border: 1px solid rgba(0, 0, 0, 0.05);
`

const ModernButton = styled(Button)`
  height: 36px;
  padding: 0px 8px !important;
  border-radius: 8px;
  font-weight: 500;
  font-size: 13px;
  border: 1px solid #e5e5e7;
  background: #fff;
  color: #1d1d1f;
  transition: all 0.2s ease;

  &:hover {
    color: #fff !important;
  }

  &.primary {
    background: #d5728a;
    border-color: #d5728a;
    color: #fff;

    &:hover {
      background: #c4596f;
      border-color: #c4596f;
      color: #fff;
    }
  }

  &.danger {
    border-color: #ff3b30;
    color: #ff3b30;

    &:hover {
      background: #ff3b30;
      color: #fff;
    }
  }
`

const ServiceCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
`

const PriceSection = styled.div`
  background: #fff;
  border-radius: 12px;
`

const CompactInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`

const CompactLabel = styled.span`
  font-size: 14px;
  color: #6c757d;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

const CompactValue = styled.span`
  font-size: 14px;
  color: #1d1d1f;
  font-weight: 500;
`

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
`

const ServiceTag = styled.span`
  background: #d5728a;
  color: #fff;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`

// 結帳紀錄商品明細：含「顯示／隱藏耗材」切換，預設摺疊耗材，
// 方便店家直接截圖給客人看（避免暴露藥水／染膏等耗材內容）。
const BookingProductsSection = ({ products = [], formatCurrency }) => {
  const [showConsumables, setShowConsumables] = useState(false)
  if (!products || products.length === 0) return null

  const consumables = products.filter((p) => p && p.isConsumable)
  const regulars = products.filter((p) => !(p && p.isConsumable))
  const visible = showConsumables ? products : regulars

  return (
    <ul
      style={{
        paddingLeft: 20,
        margin: 0,
        listStyle: 'none'
      }}
    >
      {visible.map((p, i) => {
        const qty = Number(p.quantity) || 1
        const amt =
          p.performanceAmount ??
          (p.revenueAmount != null ? Number(p.revenueAmount) * qty : null)
        return (
          <li key={(p.productId || i) + i} style={{ marginBottom: 8 }}>
            <div>
              <strong>{p.name || p.productName || '商品'}</strong>
              {p.quantity != null && <span> × {p.quantity}</span>}
              {(p.price != null || p.totalPrice != null) && (
                <span style={{ color: '#576079' }}>
                  {' '}
                  -{' '}
                  {p.totalPrice != null
                    ? formatCurrency(p.totalPrice)
                    : formatCurrency(Number(p.price || 0) * qty)}
                </span>
              )}
              {p.isConsumable && (
                <Tag
                  color='default'
                  style={{ marginLeft: 8, fontSize: 11, lineHeight: '18px' }}
                >
                  耗材
                </Tag>
              )}
            </div>
            {amt != null && Number(amt) !== 0 && (
              <div
                style={{
                  fontSize: 13,
                  color: '#8c4a00',
                  marginTop: 4,
                  paddingLeft: 8
                }}
              >
                商品業績：{formatCurrency(amt)}
              </div>
            )}
            {p.productSalesPersonnelName && (
              <div
                style={{
                  fontSize: 13,
                  color: '#576079',
                  marginTop: 4,
                  paddingLeft: 8
                }}
              >
                銷售人員：{p.productSalesPersonnelName}
              </div>
            )}
          </li>
        )
      })}
      {regulars.length === 0 && consumables.length > 0 && !showConsumables && (
        <li
          style={{
            color: '#888',
            fontSize: 13,
            paddingLeft: 0
          }}
        >
          本次共 {consumables.length} 項耗材，已隱藏
        </li>
      )}
      {consumables.length > 0 && (
        <li
          style={{
            paddingLeft: 0,
            marginTop: 4,
            textAlign: 'right'
          }}
        >
          <Button
            type='link'
            size='small'
            icon={
              showConsumables ? <EyeInvisibleOutlined /> : <EyeOutlined />
            }
            onClick={() => setShowConsumables((v) => !v)}
          >
            {showConsumables
              ? '隱藏耗材'
              : `顯示耗材（${consumables.length}）`}
          </Button>
        </li>
      )}
    </ul>
  )
}

const BookingDetailPage = ({ booking, onUpdate, onDelete }) => {
  const [editMode, setEditMode] = useState(false)
  const [editedBooking, setEditedBooking] = useState(null)
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()
  const { user, businessId } = useAuth()
  const navigate = useNavigate()
  const goBack = useGoBack('/appointments')
  const { hasFeature } = useMembership()

  // 移動設備檢測
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera
      const mobileRegex =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
      const isMobileDevice =
        mobileRegex.test(userAgent) || window.innerWidth <= 768
      setIsMobile(isMobileDevice)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // 服務相關狀態
  const [products, setProducts] = useState([])
  const [stylists, setStylists] = useState([])
  // 編輯模式選設計師用：全部員工 + 當天班表狀態（shift / off / unset）
  const [stylistRoster, setStylistRoster] = useState([])
  const [additionalServices, setAdditionalServices] = useState([])
  // 主要服務多選（取第一個作為舊欄位 fallback）
  const [selectedMainServices, setSelectedMainServices] = useState([])
  const [selectedProduct, setSelectedProduct] = useState(null) // fallback 第一個主要服務
  const [selectedAdditionalServices, setSelectedAdditionalServices] = useState(
    []
  )
  const [showServiceSelector, setShowServiceSelector] = useState(false)
  const [priceCalculation, setPriceCalculation] = useState(null)
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false)
  const [showPricePreview, setShowPricePreview] = useState(false)
  const [showPriceEditModal, setShowPriceEditModal] = useState(false)
  const [hasServiceChanges, setHasServiceChanges] = useState(false)
  const [tempPrice, setTempPrice] = useState(null)
  const [depositSettings, setDepositSettings] = useState(null)
  const [loadingDepositSettings, setLoadingDepositSettings] = useState(false)
  // 服務價格編輯狀態（serviceId -> { basePrice, actualPrice }）
  const [servicePrices, setServicePrices] = useState({})
  // 服務數量狀態（serviceId -> quantity）
  const [serviceQuantities, setServiceQuantities] = useState({})
  // 已結帳訂單詳情（當預約有 orderId 時取得）
  const [orderData, setOrderData] = useState(null)
  const [loadingOrder, setLoadingOrder] = useState(false)

  // 獲取店家訂金設置
  const fetchDepositSettings = useCallback(async () => {
    if (!businessId) return
    setLoadingDepositSettings(true)
    try {
      const response = await api.get(
        `/api/business_B/${businessId}/deposit-settings`
      )
      setDepositSettings(response.data.data || { isEnabled: false })
    } catch (error) {
      // 如果獲取失敗，默認不顯示訂金（避免誤導）
      console.error('獲取訂金設置失敗:', error)
      setDepositSettings({ isEnabled: false })
    } finally {
      setLoadingDepositSettings(false)
    }
  }, [businessId])

  useEffect(() => {
    fetchDepositSettings()
  }, [fetchDepositSettings])

  // 當預約已結帳時，取得關聯訂單詳情（服務、商品、金額、付款）
  const fetchOrderForBooking = useCallback(async () => {
    if (!booking?.orderId || !businessId) return
    setLoadingOrder(true)
    setOrderData(null)
    try {
      const response = await api.get(
        `/api/businesses/${businessId}/checkout/orders/${booking.orderId}`
      )
      const raw = response.data?.data ?? response.data
      let order = raw
      if (raw?.data && (raw.data.amounts != null || raw.data.payment != null)) {
        order = raw.data
      } else if (
        raw?.order &&
        (raw.order.amounts != null || raw.order.payment != null)
      ) {
        order = raw.order
      }
      if (order) setOrderData(order)
    } catch (e) {
      console.error('取得結帳紀錄失敗:', e)
      message.error('無法載入結帳紀錄')
    } finally {
      setLoadingOrder(false)
    }
  }, [booking?.orderId, businessId])

  useEffect(() => {
    if (booking?.orderId && businessId) fetchOrderForBooking()
    else setOrderData(null)
  }, [booking?.orderId, businessId, fetchOrderForBooking])

  // 標準化服務對象，便於 ServiceSelector 使用（參考 App 端實現）
  const normalizeServiceItem = useCallback(
    (
      service,
      fallbackCategory = 'main',
      productsData = products,
      additionalServicesData = additionalServices
    ) => {
      const serviceId = service.serviceId || service._id || service.id

      // 如果 category 已經是 'main' 或 'additional'，直接使用
      let category = service.category
      if (category !== 'main' && category !== 'additional') {
        // 否則根據 serviceId 判斷：如果在主要服務列表中，則是 main；如果在額外服務列表中，則是 additional
        const isMainService = productsData.some(
          (p) => p._id === serviceId || p.id === serviceId
        )
        const isAdditionalService = additionalServicesData.some(
          (as) =>
            as._id === serviceId ||
            as.id === serviceId ||
            as.serviceId === serviceId
        )

        category = isMainService
          ? 'main'
          : isAdditionalService
            ? 'additional'
            : fallbackCategory
      }

      return {
        serviceId,
        serviceName: service.serviceName || service.name,
        basePrice:
          service.basePrice ??
          service.price ??
          service.discountedPrice ??
          service.originalPrice ??
          0,
        duration: service.duration || 0,
        category
      }
    },
    [products, additionalServices]
  )

  // 統一傳遞給 ServiceSelector 的當前選擇，保持新增/編輯一致（參考 App 端實現）
  const currentServicesForSelector = useMemo(() => {
    const normalizedMain = selectedMainServices.map((s) =>
      normalizeServiceItem(s, 'main', products, additionalServices)
    )
    const normalizedAdditional = selectedAdditionalServices.map((s) =>
      normalizeServiceItem(s, 'additional', products, additionalServices)
    )
    const result = [...normalizedMain, ...normalizedAdditional]
    console.log('=== currentServicesForSelector 計算 ===')
    console.log('selectedMainServices:', selectedMainServices)
    console.log('selectedAdditionalServices:', selectedAdditionalServices)
    console.log('normalizedMain:', normalizedMain)
    console.log('normalizedAdditional:', normalizedAdditional)
    console.log('currentServicesForSelector 結果:', result)
    return result
  }, [
    selectedMainServices,
    selectedAdditionalServices,
    normalizeServiceItem,
    products,
    additionalServices
  ])

  // 重新獲取預約詳情
  const fetchBookingDetails = useCallback(async () => {
    if (!booking?._id) return

    try {
      setLoading(true)
      const response = await api.get(`/api/business/bookings/${booking._id}`)
      if (response.data?.success) {
        const updatedBooking = response.data.data
        setEditedBooking({ ...updatedBooking })
        // 通知父組件更新預約數據
        // if (onUpdate) {
        //   onUpdate(updatedBooking)
        // }
      }
    } catch (error) {
      console.error('獲取預約詳情失敗:', error)
    } finally {
      setLoading(false)
    }
  }, [booking?._id])

  useEffect(() => {
    if (booking) {
      console.log('完整的 booking 對象:', booking)
      setEditedBooking({
        ...booking,
        // 確保客戶信息欄位存在
        userName: booking.userName || '',
        phone: booking.phone || '',
        email: booking.email || '',
        customerId: booking.customerId || null
      })
      fetchData()
      // 進入頁面時重新獲取最新的預約詳情
      fetchBookingDetails()
    }
  }, [booking, fetchBookingDetails])

  // 當 priceCalculation 更新時，如果沒有手動修改過價格，就更新 editedBooking.discountedPrice
  useEffect(() => {
    // 如果價格修改 Modal 是打開的，不要覆蓋用戶的輸入
    if (showPriceEditModal) return

    // 只有在非編輯模式或者沒有服務變更時才更新價格
    if (
      priceCalculation?.originalTotal &&
      !hasServiceChanges &&
      !editMode &&
      editedBooking
    ) {
      setEditedBooking((prev) => ({
        ...(prev || {}),
        discountedPrice: priceCalculation.originalTotal
      }))
    }
  }, [
    priceCalculation?.originalTotal,
    hasServiceChanges,
    editMode,
    editedBooking,
    showPriceEditModal
  ])

  const fetchData = async () => {
    try {
      const [productsRes, stylistsRes, additionalServicesRes] =
        await Promise.all([
          api.get(`/api/business_B/${businessId}/products`),
          api.get(`/api/employees/${businessId}`),
          api.get(`/api/business_B/${businessId}/additional-services`)
        ])
      setProducts(productsRes.data)
      setStylists(stylistsRes.data)
      const additionalServicesData = additionalServicesRes.data?.data || []
      setAdditionalServices(additionalServicesData)

      // 數據加載完成後，初始化服務選擇
      initializeServiceSelection(productsRes.data, additionalServicesData)
    } catch (error) {
      console.error('獲取數據失敗:', error)
    }
  }


  // 編輯模式選設計師：抓「那一天」的班表狀態，分成有值班／當日休假／未排班。
  // 全部員工都列出來（休假也可指派，店家常臨時請人支援），只有未開放此服務的鎖住。
  const rosterDate = editedBooking?.date || booking?.date
  useEffect(() => {
    if (!editMode || !businessId || !rosterDate) return
    let cancelled = false
    const load = async () => {
      try {
        const dateStr =
          typeof rosterDate === 'string'
            ? rosterDate.slice(0, 10)
            : dayjs(rosterDate).format('YYYY-MM-DD')
        const serviceIds = (
          selectedMainServices.length > 0
            ? selectedMainServices.map((sv) => sv._id || sv)
            : booking?.productId
            ? [booking.productId]
            : []
        )
          .filter(Boolean)
          .map(String)
        const res = await api.get(
          `/api/employees/${businessId}/schedule/${dateStr}`,
          { params: serviceIds.length ? { serviceIds: serviceIds.join(',') } : {} }
        )
        if (!cancelled) {
          setStylistRoster(Array.isArray(res.data?.data) ? res.data.data : [])
        }
      } catch (error) {
        console.error('獲取當天班表失敗:', error)
        // 查不到班表就退回全部員工，不能讓店家改不了設計師
        if (!cancelled) {
          setStylistRoster(
            stylists.map((st) => ({
              _id: st._id,
              name: st.name,
              status: 'unset',
              shift: null,
              canServe: true
            }))
          )
        }
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [editMode, businessId, rosterDate, stylists, selectedMainServices])

  const rosterGroups = React.useMemo(() => {
    const g = { shift: [], off: [], unset: [], blocked: [] }
    for (const e of stylistRoster) {
      if (e.canServe === false) g.blocked.push(e)
      else (g[e.status] || g.unset).push(e)
    }
    return g
  }, [stylistRoster])

  const describeRosterEntry = (e) => {
    if (e.status === 'shift') {
      const time =
        e.shift?.start && e.shift?.end ? ` ${e.shift.start}–${e.shift.end}` : ''
      return `${e.name}（${e.shift?.label || '值班'}${time}）`
    }
    if (e.status === 'off') return `${e.name}（${e.shift?.label || '休假'}）`
    return e.name
  }

  // 價格計算（進入頁面與服務選擇變動時自動計算）
  // 必須分開傳主服務與加選，且加選傳 [] 時後端才會清空加選計價（不可省略 additionalServices 欄位）
  const calculatePrice = async (mainServiceIds, additionalServiceIds = []) => {
    if (!booking?._id) return
    try {
      setIsCalculatingPrice(true)

      const response = await api.post(
        `/api/business/bookings/${booking._id}/calculate-price`,
        {
          selectedServices: mainServiceIds || [],
          additionalServices: additionalServiceIds || []
        }
      )
      if (response.data?.success) {
        setPriceCalculation(response.data.priceCalculation)
      }
    } catch (error) {
      console.error('價格計算失敗:', error)
    } finally {
      setIsCalculatingPrice(false)
    }
  }

  // 初次載入與選擇變更時重新計算
  useEffect(() => {
    const isAdditionalLine = (s) =>
      (s.category || '').toString() === 'additional'

    let mainIds = []
    let additionalIds = []

    if (editMode) {
      if (
        selectedMainServices.length > 0 ||
        selectedAdditionalServices.length > 0
      ) {
        mainIds = selectedMainServices
          .map((s) => s._id || s.serviceId)
          .filter(Boolean)
        additionalIds = selectedAdditionalServices
          .map((s) => s.serviceId || s._id)
          .filter(Boolean)
      } else if (Array.isArray(booking.services)) {
        mainIds = booking.services
          .filter((s) => !isAdditionalLine(s))
          .map((s) => s.serviceId || s._id)
          .filter(Boolean)
        additionalIds = booking.services
          .filter(isAdditionalLine)
          .map((s) => s.serviceId || s._id)
          .filter(Boolean)
      } else if (
        booking.additionalServices &&
        Array.isArray(booking.additionalServices)
      ) {
        if (booking.productId) mainIds = [booking.productId]
        additionalIds = booking.additionalServices
          .map((s) => s.serviceId || s._id)
          .filter(Boolean)
      }
    } else {
      if (Array.isArray(booking.services)) {
        mainIds = booking.services
          .filter((s) => !isAdditionalLine(s))
          .map((s) => s.serviceId || s._id)
          .filter(Boolean)
        additionalIds = booking.services
          .filter(isAdditionalLine)
          .map((s) => s.serviceId || s._id)
          .filter(Boolean)
      } else if (
        booking.additionalServices &&
        Array.isArray(booking.additionalServices)
      ) {
        if (booking.productId) mainIds = [booking.productId]
        additionalIds = booking.additionalServices
          .map((s) => s.serviceId || s._id)
          .filter(Boolean)
      }
    }

    if (mainIds.length === 0 && additionalIds.length > 0 && booking.productId) {
      mainIds = [booking.productId]
    }

    if (mainIds.length > 0 || additionalIds.length > 0) {
      calculatePrice(mainIds, additionalIds)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    booking._id,
    editMode,
    JSON.stringify(selectedMainServices),
    JSON.stringify(selectedAdditionalServices)
  ])

  const initializeServiceSelection = useCallback(
    (productsData = products, additionalServicesData = additionalServices) => {
      console.log('=== initializeServiceSelection 開始 ===')
      console.log('booking.services:', booking.services)
      console.log('productsData:', productsData)
      console.log('productsData.length:', productsData?.length)
      console.log('additionalServicesData:', additionalServicesData)
      console.log(
        'additionalServicesData.length:',
        additionalServicesData?.length
      )

      // 處理新的 services 數組結構
      if (booking.services && Array.isArray(booking.services)) {
        console.log(
          'booking.services 詳細內容:',
          JSON.stringify(booking.services, null, 2)
        )

        // 判斷服務類型：如果 category 是字符串 'main' 或 'additional'，直接使用
        // 否則根據 serviceId 是否在主要服務列表或額外服務列表中來判斷
        const servicesWithCategory = booking.services.map((s) => {
          const serviceId = s.serviceId || s._id

          // 如果 category 已經是 'main' 或 'additional'，直接使用
          if (s.category === 'main' || s.category === 'additional') {
            return { ...s, category: s.category }
          }

          // 否則根據 serviceId 判斷：如果在主要服務列表中，則是 main；如果在額外服務列表中，則是 additional
          const isMainService = productsData.some(
            (p) => p._id === serviceId || p.id === serviceId
          )
          const isAdditionalService = additionalServicesData.some(
            (as) =>
              as._id === serviceId ||
              as.id === serviceId ||
              as.serviceId === serviceId
          )

          // 優先判斷：如果在主要服務列表中，則是 main；否則如果在額外服務列表中，則是 additional；否則默認為 main
          const category = isMainService
            ? 'main'
            : isAdditionalService
              ? 'additional'
              : 'main' // 默認第一個是 main

          return { ...s, category }
        })

        const mainServices = servicesWithCategory.filter(
          (s) => s.category === 'main'
        )
        const additionalServices = servicesWithCategory.filter(
          (s) => s.category === 'additional'
        )

        console.log('mainServices from booking:', mainServices)
        console.log('additionalServices from booking:', additionalServices)

        // 從 productsData 中查找完整的主要服務對象
        const fullMainServices = mainServices
          .map((s) => {
            const serviceId = s.serviceId || s._id
            console.log('查找主要服務 ID:', serviceId)
            const foundProduct = productsData.find(
              (p) => p._id === serviceId || p.id === serviceId
            )
            console.log('找到的主要服務產品:', foundProduct)
            if (foundProduct) {
              return foundProduct // 返回完整的產品對象
            }
            // 如果找不到，返回一個包含基本信息的對象
            return {
              _id: serviceId,
              id: serviceId,
              serviceId: serviceId,
              name: s.serviceName || s.name,
              serviceName: s.serviceName || s.name,
              price: s.price ?? s.basePrice,
              duration: s.duration,
              category: 'main'
            }
          })
          .filter(Boolean)

        console.log('fullMainServices 最終結果:', fullMainServices)
        setSelectedMainServices(fullMainServices)

        if (fullMainServices.length > 0) {
          setSelectedProduct(fullMainServices[0])
        } else {
          setSelectedProduct(null)
        }

        // 處理額外服務 - 從 additionalServicesData 中查找完整對象
        if (additionalServices.length > 0) {
          const fullAdditionalServices = additionalServices
            .map((s) => {
              const serviceId = s.serviceId || s._id
              console.log('查找額外服務 ID:', serviceId)
              // 從額外服務列表中查找
              const found = additionalServicesData.find(
                (as) =>
                  as._id === serviceId ||
                  as.id === serviceId ||
                  as.serviceId === serviceId
              )
              console.log('找到的額外服務:', found)
              if (found) {
                return found // 返回完整的額外服務對象
              }
              // 如果找不到，返回原始數據
              return {
                ...s,
                _id: serviceId,
                id: serviceId,
                serviceId: serviceId,
                name: s.serviceName || s.name,
                serviceName: s.serviceName || s.name,
                price: s.price ?? s.basePrice,
                duration: s.duration,
                category: 'additional'
              }
            })
            .filter(Boolean)
          console.log(
            'fullAdditionalServices 最終結果:',
            fullAdditionalServices
          )
          setSelectedAdditionalServices(fullAdditionalServices)
        } else {
          setSelectedAdditionalServices([])
        }

        // 初始化服務價格狀態（從 booking.services 中獲取）
        const initialServicePrices = {}
        const initialServiceQuantities = {}
        booking.services.forEach((s) => {
          const serviceId = s.serviceId || s._id
          if (serviceId) {
            const basePrice = s.basePrice ?? s.price ?? 0
            const actualPrice = s.actualPrice ?? s.price ?? basePrice
            const quantity = s.quantity || 1
            initialServicePrices[serviceId] = {
              basePrice,
              actualPrice
            }
            initialServiceQuantities[serviceId] = quantity
          }
        })
        setServicePrices(initialServicePrices)
        setServiceQuantities(initialServiceQuantities)
      }
      // 處理舊的數據結構 (productId + additionalServices)
      else if (booking.productId) {
        // 設置主要服務
        const foundProduct = productsData.find(
          (p) => p._id === booking.productId
        )
        if (foundProduct) {
          setSelectedProduct(foundProduct)
          setSelectedMainServices([
            {
              serviceId: foundProduct._id,
              serviceName: foundProduct.name,
              price: foundProduct.price,
              duration: foundProduct.duration,
              category: 'main'
            }
          ])
        } else {
          setSelectedProduct(null)
          setSelectedMainServices([])
        }

        // 設置額外服務
        if (
          booking.additionalServices &&
          Array.isArray(booking.additionalServices)
        ) {
          // 需要將舊格式的額外服務轉換為新格式
          const convertedAdditionalServices = booking.additionalServices.map(
            (service) => ({
              serviceId: service.serviceId || service._id,
              serviceName: service.name || service.serviceName,
              price: service.price,
              duration: service.duration,
              category: 'additional'
            })
          )
          setSelectedAdditionalServices(convertedAdditionalServices)
        } else {
          setSelectedAdditionalServices([])
        }
      } else {
        setSelectedProduct(null)
        setSelectedMainServices([])
        setSelectedAdditionalServices([])
      }

      // 初始化服務價格狀態（從 booking.services 中獲取）- 處理舊數據結構
      const initialServicePrices = {}
      if (booking.services && Array.isArray(booking.services)) {
        booking.services.forEach((s) => {
          const serviceId = s.serviceId || s._id
          if (serviceId) {
            const basePrice = s.basePrice ?? s.price ?? 0
            const actualPrice = s.actualPrice ?? s.price ?? basePrice
            initialServicePrices[serviceId] = {
              basePrice,
              actualPrice
            }
          }
        })
      }
      setServicePrices(initialServicePrices)
    },
    [booking, products, additionalServices]
  )

  // 當進入編輯模式且產品數據已加載時，初始化服務選擇
  useEffect(() => {
    if (editMode && products.length > 0 && booking) {
      initializeServiceSelection(products, additionalServices)
    }
  }, [
    editMode,
    products.length,
    booking?._id,
    initializeServiceSelection,
    additionalServices.length
  ])

  // ── 服務時長 ────────────────────────────────────────────────
  // 預設是「依服務項目加總」；店家可以勾「自訂時長」為個別客人調整
  // （例如同樣是護髮，這位客人髮量多要多留 30 分鐘）。
  // 自訂之後即使再改服務項目也不會被加總蓋掉，後端同樣以 durationMode 判斷。
  const isCustomDuration = editedBooking?.durationMode === 'manual'

  // 編輯中：依目前勾選的服務算出「自動」會是多少分鐘
  const autoDurationFromSelection = useMemo(() => {
    const mainDuration = selectedMainServices.reduce(
      (sum, service) => sum + (service.duration || 0),
      0
    )
    const additionalDuration = selectedAdditionalServices.reduce(
      (sum, service) => sum + (service.duration || 0),
      0
    )
    return mainDuration + additionalDuration
  }, [selectedMainServices, selectedAdditionalServices])

  // 檢視中：依這筆預約的服務快照算出「自動」會是多少分鐘（用來顯示提示）
  const autoDurationFromBooking = useMemo(() => {
    return (booking?.services || []).reduce(
      (sum, s) => sum + (Number(s.duration) || 0) * (Number(s.quantity) || 1),
      0
    )
  }, [booking?.services])

  const handleToggleCustomDuration = (checked) => {
    setEditedBooking((prev) => ({
      ...prev,
      durationMode: checked ? 'manual' : 'auto',
      duration: checked
        ? Number(prev?.duration) || autoDurationFromSelection || 60
        : autoDurationFromSelection
    }))
  }

  const handleEdit = () => {
    setEditMode(true)
    setEditedBooking((prev) => ({
      ...booking,
      // 確保客戶信息欄位存在
      userName: booking.userName || '',
      phone: booking.phone || '',
      email: booking.email || '',
      customerId: booking.customerId || null,
      // 將 "unspecified" 轉換為 null
      stylistId: booking.stylistId === 'unspecified' ? null : booking.stylistId,
      // 自訂時長：booking prop 有可能來自沒帶這個欄位的舊資料／舊列表，
      // 這時退回詳情 API 抓到的值，避免一進編輯就被當成「自動」而洗掉自訂時長
      durationMode: booking.durationMode || prev?.durationMode || 'auto',
      duration: booking.duration ?? prev?.duration
    }))
    // 確保在數據加載完成後初始化服務選擇
    if (products.length > 0) {
      initializeServiceSelection(products, additionalServices)
    }
  }

  const handleSave = async () => {
    // ── 防重複送出：若正在處理中則直接 return ────────────────────────────────
    if (loading) return
    setLoading(true)
    try {
      // 構建 selectedServices 數組（用於價格更新 API）
      const selectedServicesForPriceUpdate = []

      // 添加所有主要服務
      if (selectedMainServices.length > 0) {
        selectedMainServices.forEach((service) => {
          const serviceId = service._id || service.serviceId || service.id
          const basePrice = service.price || service.basePrice || 0
          const priceInfo = servicePrices[serviceId]
          const actualPrice = priceInfo?.actualPrice ?? basePrice

          selectedServicesForPriceUpdate.push({
            serviceId,
            basePrice,
            actualPrice
          })
        })
      }

      // 添加所有額外服務
      if (selectedAdditionalServices.length > 0) {
        selectedAdditionalServices.forEach((service) => {
          const serviceId = service._id || service.serviceId || service.id
          const basePrice = service.price || service.basePrice || 0
          const priceInfo = servicePrices[serviceId]
          const actualPrice = priceInfo?.actualPrice ?? basePrice

          selectedServicesForPriceUpdate.push({
            serviceId,
            basePrice,
            actualPrice
          })
        })
      }

      // 構建新的服務結構（使用 selectedMainServices 和 selectedAdditionalServices）
      const servicesArray = []

      // 添加所有主要服務
      if (selectedMainServices.length > 0) {
        selectedMainServices.forEach((service) => {
          const serviceId = service._id || service.serviceId || service.id
          const basePrice = service.price || service.basePrice || 0
          const priceInfo = servicePrices[serviceId]
          const actualPrice = priceInfo?.actualPrice ?? basePrice
          const quantity = serviceQuantities[serviceId] || 1

          servicesArray.push({
            serviceId,
            serviceName: service.name || service.serviceName,
            basePrice,
            actualPrice,
            quantity,
            duration: service.duration || 0,
            category: 'main'
          })
        })
      }

      // 添加所有額外服務
      if (selectedAdditionalServices.length > 0) {
        selectedAdditionalServices.forEach((service) => {
          const serviceId = service._id || service.serviceId || service.id
          const basePrice = service.price || service.basePrice || 0
          const priceInfo = servicePrices[serviceId]
          const actualPrice = priceInfo?.actualPrice ?? basePrice
          const quantity = serviceQuantities[serviceId] || 1

          servicesArray.push({
            serviceId,
            serviceName: service.name || service.serviceName,
            basePrice,
            actualPrice,
            quantity,
            duration: service.duration || 0,
            category: 'additional'
          })
        })
      }

      // 計算總價和總時長（使用 actualPrice * quantity）
      const totalPrice = selectedServicesForPriceUpdate.reduce((sum, s) => {
        const quantity = serviceQuantities[s.serviceId || s._id] || 1
        return sum + (s.actualPrice || 0) * quantity
      }, 0)
      const totalDuration =
        selectedMainServices.reduce(
          (sum, service) => sum + (service.duration || 0),
          0
        ) +
        selectedAdditionalServices.reduce(
          (sum, service) => sum + (service.duration || 0),
          0
        )
      const hasServicePricePayload =
        servicesArray.length > 0 || selectedServicesForPriceUpdate.length > 0
      const safeOriginalPrice = hasServicePricePayload
        ? totalPrice
        : Number(booking.originalPrice) || 0
      const safeDiscountedPrice = hasServicePricePayload
        ? (editedBooking.discountedPrice ?? totalPrice)
        : (editedBooking.discountedPrice ??
          booking.discountedPrice ??
          booking.originalPrice ??
          0)

      const servicesChanged =
        JSON.stringify(servicesArray) !== JSON.stringify(booking.services || [])
      const priceChanged =
        editedBooking.discountedPrice !== booking.discountedPrice
      const stylistChanged = editedBooking.stylistId !== booking.stylistId
      const dateChanged = editedBooking.date !== booking.date
      const timeChanged = editedBooking.time !== booking.time

      // 檢查客戶信息是否變更
      const customerNameChanged = editedBooking.userName !== booking.userName

      const updatedData = {
        // 基本預約信息
        status: editedBooking.status,
        date: editedBooking.date,
        time: editedBooking.time,
        stylistId: editedBooking.stylistId,
        note: editedBooking.note,

        // 客戶信息
        userName: editedBooking.userName || booking.userName,
        phone: editedBooking.phone || booking.phone,
        email: editedBooking.email || booking.email,
        customerId: editedBooking.customerId || booking.customerId,

        // 定金相關（只有當店家啟用訂金功能時才保存）
        ...(depositSettings?.isEnabled && {
          depositStatus: editedBooking.depositStatus,
          // 只在標成已付時才送收款方式；後端也只在那個情況下才寫入
          ...(editedBooking.depositStatus === 'paid' &&
            editedBooking.depositPaymentMethod && {
              depositPaymentMethod: editedBooking.depositPaymentMethod
            })
        }),

        // 價格相關
        originalPrice: safeOriginalPrice,
        discountedPrice: safeDiscountedPrice,

        // 服務修改相關 - 新的服務結構
        services: servicesArray,
        // 發送 selectedServices 數組用於價格更新（包含 serviceId, basePrice, actualPrice）
        selectedServices: selectedServicesForPriceUpdate,
        additionalServices: servicesArray
          .filter((service) => service.category === 'additional')
          .map((service) => service.serviceId),

        // 向後兼容字段（使用第一個主要服務）
        productId:
          selectedMainServices.length > 0
            ? selectedMainServices[0]._id ||
              selectedMainServices[0].serviceId ||
              selectedMainServices[0].id
            : null,
        productName:
          selectedMainServices.length > 0
            ? selectedMainServices
                .map((s) => s.name || s.serviceName)
                .join('、')
            : null,
        // 服務時長：自訂時送店家輸入的值，後端會以 durationMode 判斷要不要沿用
        durationMode: isCustomDuration ? 'manual' : 'auto',
        duration: isCustomDuration
          ? Number(editedBooking.duration) || totalDuration
          : totalDuration,

        // 變更標記
        servicesChanged,
        priceChanged,
        stylistChanged,
        dateChanged,
        timeChanged,
        customerNameChanged
      }

      // 使用新的 API 端點更新預約服務價格
      const response = await api.put(
        `/api/business/bookings/${booking._id}`,
        updatedData
      )

      setEditMode(false)
      // 處理 API 響應（可能返回 response.data.data 或 response.data）
      const updatedBooking = response.data?.data || response.data
      if (onUpdate && updatedBooking) {
        onUpdate(updatedBooking)
      }

      let successMessage = '預約資料已更新'
      const hasImportantChanges =
        servicesChanged ||
        priceChanged ||
        stylistChanged ||
        dateChanged ||
        timeChanged

      if (hasImportantChanges) {
        const changes = []
        if (servicesChanged) changes.push('服務項目')
        if (priceChanged) changes.push('價格')
        if (stylistChanged) changes.push('設計師')
        if (dateChanged) changes.push('預約日期')
        if (timeChanged) changes.push('預約時間')

        successMessage = `${changes.join('和')}已更新，客戶將收到通知`
      }

      message.success(successMessage)

      // 編輯完成後重新獲取最新的預約詳情
      await fetchBookingDetails()
    } catch (error) {
      console.error('更新預約失敗:', error)
      message.error('更新預約失敗')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setEditMode(false)
    setEditedBooking({
      ...booking,
      // 確保客戶信息欄位存在
      userName: booking.userName || '',
      phone: booking.phone || '',
      email: booking.email || '',
      customerId: booking.customerId || null
    })
    setHasServiceChanges(false) // 重置服務變更標記
    // 重置服務價格狀態
    const resetServicePrices = {}
    if (booking.services && Array.isArray(booking.services)) {
      booking.services.forEach((s) => {
        const serviceId = s.serviceId || s._id
        if (serviceId) {
          const basePrice = s.basePrice ?? s.price ?? 0
          const actualPrice = s.actualPrice ?? s.price ?? basePrice
          resetServicePrices[serviceId] = {
            basePrice,
            actualPrice
          }
        }
      })
    }
    setServicePrices(resetServicePrices)
    // 確保在數據加載完成後初始化服務選擇
    if (products.length > 0) {
      initializeServiceSelection(products, additionalServices)
    }
  }

  const handleDelete = () => {
    Modal.confirm({
      title: '確認刪除',
      content: '確定要刪除這個預約嗎？此操作無法復原。',
      okText: '確認',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: () => onDelete(booking._id)
    })
  }

  // 取消訂單交易
  const handleCancelOrder = async (orderId, bookingId) => {
    try {
      await api.post(
        `/api/businesses/${businessId}/checkout/orders/${orderId}/void`,
        {
          reason: '取消交易',
          bookingId: bookingId
        }
      )
      message.success('訂單已取消')
      // 重新獲取預約詳情以更新狀態
      if (onUpdate) {
        const response = await api.get(`/api/business/bookings/${bookingId}`)
        if (response.data?.success) {
          onUpdate(response.data.data)
        }
      }
    } catch (error) {
      console.error('取消訂單失敗:', error)
      message.error('取消訂單失敗')
      throw error
    }
  }

  // 重新結帳（優先傳遞 orderId，結帳頁會從訂單詳情載入完整內容）
  const handleRecheckout = async () => {
    if (!booking.orderId) return

    Modal.confirm({
      title: '重新結帳',
      content:
        '此操作將取消原訂單並重新結帳，原訂單的消費內容（服務、商品、客戶、付款方式等）將自動帶入結帳頁面。確定繼續嗎？',
      okText: '確認',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await handleCancelOrder(booking.orderId, booking._id)
          const params = new URLSearchParams({ recheckout: 'true' })
          params.set('orderId', booking.orderId)
          params.set('bookingId', booking._id)
          navigate(`/checkout/checkout?${params.toString()}`)
        } catch (error) {
          message.error('操作失敗')
        }
      }
    })
  }

  // 僅取消交易
  const handleCancelOnly = async () => {
    if (!booking.orderId) return

    Modal.confirm({
      title: '確認取消交易',
      content: `確定要取消訂單 ${booking.orderId} 的交易嗎？此操作將作廢該訂單。`,
      okText: '確認',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await handleCancelOrder(booking.orderId, booking._id)
        } catch (error) {
          // 錯誤已在 handleCancelOrder 中處理
        }
      }
    })
  }

  // 預約狀態選項
  const statusOptions = [
    { value: 'pending', label: '等待確認' },
    { value: 'confirmed', label: '已確認' },
    { value: 'completed', label: '已完成' },
    { value: 'canceled', label: '已取消' },
    { value: 'no_show', label: '未出席' }
  ]

  const getStatusConfig = (status) => {
    switch (status) {
      case 'pending':
        return { color: '#ff9500', textColor: '#fff', text: '等待確認' }
      case 'confirmed':
        return { color: '#34c759', textColor: '#fff', text: '已確認' }
      case 'completed':
        return { color: '#007aff', textColor: '#fff', text: '已完成' }
      case 'canceled':
        return { color: '#ff3b30', textColor: '#fff', text: '已取消' }
      case 'no_show':
        return { color: '#af52de', textColor: '#fff', text: '未出席' }
      default:
        return { color: '#f5f5f7', textColor: '#1d1d1f', text: status || '-' }
    }
  }

  const getDepositStatusConfig = (status) => {
    switch (status) {
      case 'paid':
        return { color: '#34c759', textColor: '#fff', text: '已付款' }
      case 'unpaid':
        return { color: '#ff3b30', textColor: '#fff', text: '待付款' }
      case 'not_required':
        return { color: '#007aff', textColor: '#fff', text: '免訂金' }
      default:
        return { color: '#f5f5f7', textColor: '#1d1d1f', text: status || '-' }
    }
  }

  const getConfirmationStatusConfig = (status) => {
    switch (status) {
      case 'pending':
        return { color: '#ff9500', textColor: '#fff', text: '待確認' }
      case 'confirmed':
        return { color: '#34c759', textColor: '#fff', text: '已確認' }
      case 'canceled':
        return { color: '#ff3b30', textColor: '#fff', text: '已取消' }
      default:
        return { color: '#f5f5f7', textColor: '#1d1d1f', text: status || '-' }
    }
  }

  if (!booking) {
    return null
  }

  return (
    <PageContainer>
      <GlobalStyles />
      <HeaderSection>
        <BackButton
          icon={<ArrowLeftOutlined />}
          onClick={goBack}
        ></BackButton>
        <HeaderTitle></HeaderTitle>
        <div style={{ display: 'flex', gap: '8px' }}>
          {editMode ? (
            <>
              <ModernButton onClick={handleCancel} disabled={loading}>
                取消
              </ModernButton>
              <ModernButton
                className='primary'
                onClick={handleSave}
                loading={loading}
                icon={<SaveOutlined />}
              >
                保存
              </ModernButton>
            </>
          ) : (
            <>
              {booking.hasCheckedOut && hasFeature('checkout') ? (
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'cancel',
                        label: '取消交易',
                        icon: <CloseCircleOutlined />,
                        onClick: handleCancelOnly
                      },
                      {
                        key: 'recheckout',
                        label: '重新結帳',
                        icon: <ReloadOutlined />,
                        onClick: handleRecheckout
                      }
                    ]
                  }}
                  trigger={['click']}
                  placement='bottomRight'
                >
                  <ModernButton
                    className='primary'
                    icon={<CheckCircleOutlined />}
                    style={{ background: '#34c759', borderColor: '#34c759' }}
                  >
                    已結帳{' '}
                    <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
                  </ModernButton>
                </Dropdown>
              ) : (
                !booking.hasCheckedOut && (
                  <ModernButton
                    className='primary'
                    onClick={() => {
                      navigate(`/checkout/checkout?bookingId=${booking._id}`)
                    }}
                    icon={<DollarOutlined />}
                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                  >
                    結帳
                  </ModernButton>
                )
              )}
              <ModernButton
                className='danger'
                onClick={handleDelete}
                icon={<DeleteOutlined />}
              >
                刪除
              </ModernButton>
              <ModernButton
                className='primary'
                onClick={handleEdit}
                icon={<EditOutlined />}
              >
                編輯
              </ModernButton>
            </>
          )}
        </div>
      </HeaderSection>

      <ContentSection>
        {/* 狀態和基本信息 */}
        <ModernCard>
          <SectionTitle>
            <InfoCircleOutlined />
            預約詳情
          </SectionTitle>
          <div>
            <CompactInfoRow>
              <CompactLabel>預約狀態</CompactLabel>
              <CompactValue>
                {editMode ? (
                  <Select
                    value={editedBooking.status || booking.status}
                    onChange={(value) =>
                      setEditedBooking({
                        ...editedBooking,
                        status: value
                      })
                    }
                    style={{ width: '150px' }}
                  >
                    {statusOptions.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                ) : (
                  <StatusBadge {...getStatusConfig(booking.status)}>
                    {getStatusConfig(booking.status).text}
                  </StatusBadge>
                )}
              </CompactValue>
            </CompactInfoRow>
            {/* 只有當預約來源不是店家新增時才顯示客戶確認狀態 */}
            {booking.confirmationStatus &&
              booking.bookingSource !== 'business' && (
                <CompactInfoRow>
                  <CompactLabel>客戶確認狀態</CompactLabel>
                  <CompactValue>
                    <StatusBadge
                      {...getConfirmationStatusConfig(
                        booking.confirmationStatus
                      )}
                    >
                      {
                        getConfirmationStatusConfig(booking.confirmationStatus)
                          .text
                      }
                    </StatusBadge>
                  </CompactValue>
                </CompactInfoRow>
              )}
            <CompactInfoRow>
              <CompactLabel>服務項目</CompactLabel>
              <CompactValue>
                {booking.services &&
                Array.isArray(booking.services) &&
                booking.services.length > 0
                  ? booking.services
                      .filter((s) => s.category !== 'additional') // 非加選皆視為主要服務
                      .map((s) => {
                        const quantity = s.quantity || 1
                        return quantity > 1
                          ? `${s.serviceName} × ${quantity}`
                          : s.serviceName
                      })
                      .join('、') || booking.productName
                  : booking.productName || '無服務'}
              </CompactValue>
            </CompactInfoRow>
            <CompactInfoRow>
              <CompactLabel>預約人數</CompactLabel>
              <CompactValue>{booking.participantCount || 1} 人</CompactValue>
            </CompactInfoRow>
            {booking.additionalServices &&
              booking.additionalServices.length > 0 && (
                <CompactInfoRow>
                  <CompactLabel>加選服務</CompactLabel>
                  <CompactValue style={{ color: '#6c757d' }}>
                    {booking.additionalServices
                      .map((service) => {
                        const quantity = service.quantity || 1
                        return quantity > 1
                          ? `${service.serviceName} × ${quantity}`
                          : service.serviceName
                      })
                      .join(', ')}
                  </CompactValue>
                </CompactInfoRow>
              )}
            {booking.services &&
              Array.isArray(booking.services) &&
              booking.services.some((s) => s.category === 'additional') && (
                <CompactInfoRow>
                  <CompactLabel>加選服務</CompactLabel>
                  <CompactValue style={{ color: '#6c757d' }}>
                    {booking.services
                      .filter((s) => s.category === 'additional')
                      .map((service) => {
                        const quantity = service.quantity || 1
                        return quantity > 1
                          ? `${service.serviceName} × ${quantity}`
                          : service.serviceName
                      })
                      .join(', ')}
                  </CompactValue>
                </CompactInfoRow>
              )}
            <CompactInfoRow>
              <CompactLabel>預約日期</CompactLabel>
              <CompactValue>
                {editMode ? (
                  <DatePicker
                    value={
                      editedBooking.date ? dayjs(editedBooking.date) : null
                    }
                    onChange={(date) =>
                      setEditedBooking({
                        ...editedBooking,
                        date: date ? date.format('YYYY-MM-DD') : null
                      })
                    }
                    format='YYYY年MM月DD日'
                    style={{ width: '150px' }}
                    inputReadOnly={isMobile} // 手機端只讀輸入框
                    placeholder='選擇預約日期'
                  />
                ) : (
                  dayjs(booking.date).format('YYYY年MM月DD日')
                )}
              </CompactValue>
            </CompactInfoRow>
            <CompactInfoRow>
              <CompactLabel>預約時間</CompactLabel>
              <CompactValue>
                {editMode ? (
                  <TimePicker
                    value={
                      editedBooking.time
                        ? dayjs(editedBooking.time, 'HH:mm')
                        : null
                    }
                    onChange={(time) =>
                      setEditedBooking({
                        ...editedBooking,
                        time: time ? time.format('HH:mm') : null
                      })
                    }
                    format='HH:mm'
                    style={{ width: '100%' }}
                    inputReadOnly={isMobile} // 手機端只讀輸入框
                    placeholder='選擇預約時間'
                    minuteStep={15} // 15分鐘間隔
                  />
                ) : (
                  booking.time
                )}
              </CompactValue>
            </CompactInfoRow>
            <CompactInfoRow>
              <CompactLabel>預約編號</CompactLabel>
              <CompactValue style={{ fontSize: '11px', color: '#6c757d' }}>
                {booking._id.slice(-5).toUpperCase()}
              </CompactValue>
            </CompactInfoRow>
            {booking.bookingSource && (
              <CompactInfoRow>
                <CompactLabel>預約來源</CompactLabel>
                <CompactValue>
                  {booking.bookingSource === 'business'
                    ? '店家新增'
                    : '客人線上預約'}
                </CompactValue>
              </CompactInfoRow>
            )}
            {/* LINE_CONTACT_PROMPT：客人按了「傳送預約資訊給店家」，訊息已進本店 LINE 官方帳號聊天室 */}
            {booking.lineContact?.contactedAt && (
              <CompactInfoRow>
                <CompactLabel>LINE 聯繫</CompactLabel>
                <CompactValue>
                  <StatusBadge color='#06c755' textColor='#fff'>
                    ✓ 客人已在 LINE 聯繫
                  </StatusBadge>
                  <span
                    style={{
                      marginLeft: '8px',
                      fontSize: '12px',
                      color: '#6c757d'
                    }}
                  >
                    {formatTz(booking.lineContact.contactedAt, 'MM/DD HH:mm')}
                    ・請到 LINE 官方帳號聊天室回覆
                  </span>
                </CompactValue>
              </CompactInfoRow>
            )}
            {booking.hasCheckedOut && (
              <CompactInfoRow>
                <CompactLabel>結帳狀態</CompactLabel>
                <CompactValue>
                  <StatusBadge color='#34c759' textColor='#fff'>
                    ✓ 已結帳
                  </StatusBadge>
                  {booking.orderId && (
                    <span
                      style={{
                        marginLeft: '8px',
                        fontSize: '12px',
                        color: '#6c757d'
                      }}
                    >
                      (訂單：{booking.orderId})
                    </span>
                  )}
                </CompactValue>
              </CompactInfoRow>
            )}
            {booking.orderStatus && (
              <CompactInfoRow>
                <CompactLabel>訂單狀態</CompactLabel>
                <CompactValue>
                  {booking.orderStatus === 'paid'
                    ? '已付款'
                    : booking.orderStatus === 'pending'
                      ? '待付款'
                      : booking.orderStatus}
                </CompactValue>
              </CompactInfoRow>
            )}
            {booking.isRated !== undefined &&
              booking.bookingSource !== 'business' && (
                <CompactInfoRow>
                  <CompactLabel>評價狀態</CompactLabel>
                  <CompactValue>
                    {booking.isRated ? '✓ 已評價' : '未評價'}
                  </CompactValue>
                </CompactInfoRow>
              )}
            {booking.duration && (
              <CompactInfoRow>
                <CompactLabel>服務時長</CompactLabel>
                <CompactValue>
                  {booking.duration} 分鐘
                  {booking.durationMode === 'manual' && (
                    <Tag
                      color='#d5728a'
                      style={{ marginLeft: '6px', borderRadius: '10px' }}
                    >
                      自訂
                    </Tag>
                  )}
                </CompactValue>
              </CompactInfoRow>
            )}
          </div>
        </ModernCard>

        {/* 施作同意書（未開通模組時自動隱藏） */}
        <ConsentFormBookingSection
          businessId={businessId}
          bookingId={booking._id}
        />

        {/* 客戶信息 */}
        <ModernCard>
          <SectionTitle>
            <InfoCircleOutlined />
            客戶資訊
          </SectionTitle>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>客戶姓名</InfoLabel>
              <InfoValue>
                {editMode && editedBooking ? (
                  <Input
                    value={editedBooking.userName || ''}
                    onChange={(e) =>
                      setEditedBooking({
                        ...editedBooking,
                        userName: e.target.value
                      })
                    }
                    placeholder='請輸入客戶姓名'
                  />
                ) : booking?.customerId ? (
                  <CustomerNameLink
                    type='button'
                    onClick={() =>
                      navigate(`/customers/${String(booking.customerId)}`)
                    }
                  >
                    {booking?.userName || '-'}
                  </CustomerNameLink>
                ) : (
                  booking?.userName || '-'
                )}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>生日</InfoLabel>
              <InfoValue>
                {formatTz(booking.customerBirthdate, 'YYYY-MM-DD')}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>聯絡電話</InfoLabel>
              <InfoValue>
                {editMode && editedBooking ? (
                  <Input
                    value={editedBooking.phone || ''}
                    onChange={(e) =>
                      setEditedBooking({
                        ...editedBooking,
                        phone: e.target.value
                      })
                    }
                    placeholder='請輸入聯絡電話'
                  />
                ) : (
                  <>
                    {booking?.phone || '-'}
                    {/*
                      只在明確為 false 時顯示。undefined 代表這筆單沒有這項資訊
                      （這個欄位上線前的舊單，或店家自己在後台建立的單），
                      那種情況顯示「未驗證」會把當時其實驗過的客人誤標。
                    */}
                    {booking?.phoneVerified === false && (
                      <Tooltip title='這位客人用 LINE 或 Google 登入預約，號碼是自己填寫的，沒有經過簡訊驗證。首次到店前建議先電話或訊息確認一次。'>
                        <Tag
                          color='warning'
                          style={{ marginLeft: 8, cursor: 'help' }}
                        >
                          手機未驗證
                        </Tag>
                      </Tooltip>
                    )}
                  </>
                )}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>電子郵件</InfoLabel>
              <InfoValue>
                {editMode && editedBooking ? (
                  <Input
                    value={editedBooking.email || ''}
                    onChange={(e) =>
                      setEditedBooking({
                        ...editedBooking,
                        email: e.target.value
                      })
                    }
                    placeholder='請輸入電子郵件'
                    type='email'
                  />
                ) : (
                  booking?.email || '-'
                )}
              </InfoValue>
            </InfoItem>
          </InfoGrid>
        </ModernCard>

        {/* 結帳紀錄（預約已結帳時顯示關聯訂單） */}
        {booking?.orderId && (
          <ModernCard>
            <SectionTitle>
              <DollarOutlined />
              結帳紀錄
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 12,
                  fontWeight: 400,
                  color: '#6c757d'
                }}
              >
                訂單：{booking.orderId}
              </span>
            </SectionTitle>
            {loadingOrder ? (
              <div style={{ padding: 24, textAlign: 'center' }}>
                <Spin />
              </div>
            ) : orderData ? (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
              >
                {(() => {
                  const formatCurrency = (v) =>
                    v == null || v === ''
                      ? 'NT$ 0'
                      : `NT$ ${Number(v).toLocaleString()}`
                  const services = orderData.services ?? orderData.items ?? []
                  const products = orderData.products ?? []
                  const payment = orderData.payment || {}
                  const totals = orderData.totals || {}
                  const amounts = orderData.amounts || {}
                  const couponDeduction = Number(payment.voucher) || 0
                  const appliedCoupon =
                    orderData.appliedStoreCoupon || orderData.appliedUserCoupon || null

                  return (
                    <>
                      {/* 服務明細 */}
                      {services.length > 0 && (
                        <div>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: '#1d1d1f',
                              marginBottom: 8
                            }}
                          >
                            服務明細
                          </div>
                          <ul
                            style={{
                              paddingLeft: 20,
                              margin: 0,
                              listStyle: 'none'
                            }}
                          >
                            {services.map((s, i) => {
                              const qty = Number(s.quantity) || 1
                              const unitPrice =
                                s.price !== undefined && s.price !== null
                                  ? Number(s.price)
                                  : Number(s.originalPrice) || 0
                              const lineTotal = unitPrice * qty
                              const amt =
                                s.performanceAmount ??
                                (s.revenueAmount != null
                                  ? Number(s.revenueAmount) * qty
                                  : null)
                              const designerName =
                                s.designerName ??
                                (typeof s.designerId === 'object' &&
                                s.designerId !== null
                                  ? s.designerId.name
                                  : null) ??
                                (s.designerId
                                  ? (stylists.find(
                                      (e) =>
                                        e._id ===
                                        (s.designerId?._id ?? s.designerId)
                                    )?.name ?? null)
                                  : null)
                              const assistantName =
                                s.assistantName ??
                                (s.assistantId
                                  ? (stylists.find(
                                      (e) =>
                                        e._id ===
                                        (s.assistantId?._id ?? s.assistantId)
                                    )?.name ?? null)
                                  : null)
                              return (
                                <li
                                  key={(s.serviceId || i) + i}
                                  style={{ marginBottom: 10 }}
                                >
                                  <div>
                                    <strong>
                                      {s.name || s.serviceName || '服務項目'}
                                    </strong>{' '}
                                    <span style={{ color: '#576079' }}>
                                      {formatCurrency(unitPrice)}
                                      {qty > 1
                                        ? ` x${qty} = ${formatCurrency(lineTotal)}`
                                        : ''}
                                    </span>
                                  </div>
                                  {amt != null && Number(amt) !== 0 && (
                                    <div
                                      style={{
                                        fontSize: 13,
                                        color: '#8c4a00',
                                        marginTop: 4,
                                        paddingLeft: 8
                                      }}
                                    >
                                      應用業績：{formatCurrency(amt)}
                                    </div>
                                  )}
                                  {(designerName || assistantName) && (
                                    <div
                                      style={{
                                        fontSize: 13,
                                        color: '#576079',
                                        marginTop: 4,
                                        paddingLeft: 8
                                      }}
                                    >
                                      {designerName && (
                                        <span style={{ marginRight: 12 }}>
                                          設計師：{designerName}
                                        </span>
                                      )}
                                      {assistantName && (
                                        <span>助理：{assistantName}</span>
                                      )}
                                    </div>
                                  )}
                                </li>
                              )
                            })}
                          </ul>
                        </div>
                      )}

                      {/* 商品明細 */}
                      {products.length > 0 && (
                        <div>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: '#1d1d1f',
                              marginBottom: 8
                            }}
                          >
                            商品明細
                          </div>
                          <BookingProductsSection
                            products={products}
                            formatCurrency={formatCurrency}
                          />
                        </div>
                      )}

                      {/* 金額資訊 */}
                      <div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: '#1d1d1f',
                            marginBottom: 8
                          }}
                        >
                          金額資訊
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                            fontSize: 14
                          }}
                        >
                          {(totals.servicesAmount != null ||
                            totals.subtotal != null) && (
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between'
                              }}
                            >
                              <span>服務小計</span>
                              <span>
                                {formatCurrency(
                                  totals.servicesAmount ?? totals.subtotal
                                )}
                              </span>
                            </div>
                          )}
                          {(totals.productsAmount != null ||
                            totals.productsAmount === 0) && (
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between'
                              }}
                            >
                              <span>商品小計</span>
                              <span>
                                {formatCurrency(totals.productsAmount)}
                              </span>
                            </div>
                          )}
                          {(amounts.receivable != null ||
                            payment.receivable != null) && (
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontWeight: 600
                              }}
                            >
                              <span>應收金額</span>
                              <span>
                                {formatCurrency(
                                  amounts.receivable ?? payment.receivable
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {(couponDeduction > 0 || appliedCoupon) && (
                        <div>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: '#1d1d1f',
                              marginBottom: 8
                            }}
                          >
                            優惠券折抵
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 6,
                              fontSize: 14
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between'
                              }}
                            >
                              <span>折抵金額</span>
                              <span style={{ fontWeight: 600 }}>
                                {formatCurrency(couponDeduction)}
                              </span>
                            </div>
                            {appliedCoupon?.code && (
                              <div>
                                <span style={{ color: '#576079' }}>優惠碼：</span>
                                <strong>{appliedCoupon.code}</strong>
                              </div>
                            )}
                            {appliedCoupon?.description && (
                              <div>
                                <span style={{ color: '#576079' }}>說明：</span>
                                <strong>{appliedCoupon.description}</strong>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* 付款方式 */}
                      {(() => {
                        const labels = [
                          { key: 'storedValue', label: '儲值金' },
                          { key: 'voucher', label: '票券' },
                          { key: 'cash', label: '現金' },
                          { key: 'transfer', label: '匯款' },
                          { key: 'deposit', label: '定金' },
                          { key: 'bonus', label: '紅利金' },
                          { key: 'debt', label: '欠款' },
                          { key: 'installment', label: '分期' }
                        ]
                        const entries = labels
                          .map(({ key, label }) => ({
                            key,
                            label,
                            amount: Number(payment[key]) || 0
                          }))
                          .filter((e) => e.amount > 0)
                        if (!entries.length) return null
                        return (
                          <div>
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: '#1d1d1f',
                                marginBottom: 8
                              }}
                            >
                              付款方式
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 6,
                                fontSize: 14
                              }}
                            >
                              {entries.map((e) => (
                                <div
                                  key={e.key}
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between'
                                  }}
                                >
                                  <span>{e.label}</span>
                                  <span style={{ fontWeight: 500 }}>
                                    {formatCurrency(e.amount)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      })()}
                    </>
                  )
                })()}
              </div>
            ) : (
              <div style={{ fontSize: 14, color: '#6c757d' }}>
                無法載入結帳紀錄
              </div>
            )}
          </ModernCard>
        )}

        {/* 服務信息 */}
        <ModernCard>
          <SectionTitle>
            <PlusOutlined />
            服務資訊
          </SectionTitle>

          {editMode ? (
            <div>
              <Button
                type='dashed'
                icon={<PlusOutlined />}
                onClick={() => {
                  console.log('=== 點擊修改服務項目按鈕 ===')
                  console.log('selectedMainServices:', selectedMainServices)
                  console.log(
                    'selectedAdditionalServices:',
                    selectedAdditionalServices
                  )
                  console.log('selectedProduct:', selectedProduct)
                  console.log('booking.services:', booking.services)
                  setShowServiceSelector(true)
                }}
                style={{ width: '100%', height: '36px', marginBottom: '12px' }}
              >
                修改服務項目
              </Button>

              {/* 顯示所有主要服務 */}
              {selectedMainServices.length > 0 && (
                <ServiceCard>
                  {selectedMainServices.map((service, index) => {
                    const serviceId =
                      service._id || service.serviceId || service.id
                    const basePrice = service.price || service.basePrice || 0
                    const priceInfo = servicePrices[serviceId] || {
                      basePrice,
                      actualPrice: basePrice
                    }

                    return (
                      <div
                        key={index}
                        style={{
                          marginBottom:
                            index < selectedMainServices.length - 1 ? 16 : 0
                        }}
                      >
                        <CompactInfoRow>
                          <CompactLabel>
                            {service.name || service.serviceName}
                          </CompactLabel>
                          <CompactValue>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                              }}
                            >
                              <span style={{ fontSize: 12, color: '#6c757d' }}>
                                原價: ${basePrice}
                              </span>
                              <InputNumber
                                key={serviceId}
                                min={0}
                                precision={0}
                                step={1}
                                value={
                                  servicePrices[serviceId]?.actualPrice ??
                                  basePrice
                                }
                                onChange={(value) => {
                                  // 簡化邏輯：如果使用者清空（null），設置為 0
                                  const newActualPrice =
                                    value === null || value === undefined
                                      ? 0
                                      : value

                                  const updatedServicePrices = {
                                    ...servicePrices,
                                    [serviceId]: {
                                      basePrice:
                                        priceInfo.basePrice || basePrice,
                                      actualPrice: newActualPrice
                                    }
                                  }
                                  setServicePrices(updatedServicePrices)

                                  // 計算所有服務的總價（使用 actualPrice）
                                  const allServices = [
                                    ...selectedMainServices,
                                    ...selectedAdditionalServices
                                  ]
                                  const newTotalPrice = allServices.reduce(
                                    (sum, s) => {
                                      const sId = s._id || s.serviceId || s.id
                                      const sBasePrice =
                                        s.price || s.basePrice || 0
                                      const currentSActualPrice =
                                        sId === serviceId
                                          ? newActualPrice
                                          : (updatedServicePrices[sId]
                                              ?.actualPrice ?? sBasePrice)
                                      return sum + Number(currentSActualPrice)
                                    },
                                    0
                                  )

                                  // 自動更新結帳總金額
                                  setEditedBooking((prev) => ({
                                    ...prev,
                                    discountedPrice: newTotalPrice
                                  }))
                                }}
                                style={{ width: 120 }}
                                prefix='$'
                              />
                            </div>
                          </CompactValue>
                        </CompactInfoRow>
                      </div>
                    )
                  })}
                </ServiceCard>
              )}

              {/* 顯示所有額外服務 */}
              {selectedAdditionalServices.length > 0 && (
                <ServiceCard>
                  {selectedAdditionalServices.map((service, index) => {
                    const serviceId =
                      service._id || service.serviceId || service.id
                    const basePrice = service.price || service.basePrice || 0
                    const priceInfo = servicePrices[serviceId] || {
                      basePrice,
                      actualPrice: basePrice
                    }

                    return (
                      <div
                        key={index}
                        style={{
                          marginBottom:
                            index < selectedAdditionalServices.length - 1
                              ? 16
                              : 0
                        }}
                      >
                        <CompactInfoRow>
                          <CompactLabel>
                            {service.name || service.serviceName}
                          </CompactLabel>
                          <CompactValue>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                              }}
                            >
                              <span style={{ fontSize: 12, color: '#6c757d' }}>
                                原價: ${basePrice}
                              </span>
                              <InputNumber
                                key={serviceId}
                                min={0}
                                precision={0}
                                step={1}
                                value={
                                  servicePrices[serviceId]?.actualPrice ??
                                  basePrice
                                }
                                onChange={(value) => {
                                  // 簡化邏輯：如果使用者清空（null），設置為 0
                                  const newActualPrice =
                                    value === null || value === undefined
                                      ? 0
                                      : value

                                  const updatedServicePrices = {
                                    ...servicePrices,
                                    [serviceId]: {
                                      basePrice:
                                        priceInfo.basePrice || basePrice,
                                      actualPrice: newActualPrice
                                    }
                                  }
                                  setServicePrices(updatedServicePrices)

                                  // 計算所有服務的總價（使用 actualPrice）
                                  const allServices = [
                                    ...selectedMainServices,
                                    ...selectedAdditionalServices
                                  ]
                                  const newTotalPrice = allServices.reduce(
                                    (sum, s) => {
                                      const sId = s._id || s.serviceId || s.id
                                      const sBasePrice =
                                        s.price || s.basePrice || 0
                                      const currentSActualPrice =
                                        sId === serviceId
                                          ? newActualPrice
                                          : (updatedServicePrices[sId]
                                              ?.actualPrice ?? sBasePrice)
                                      return sum + Number(currentSActualPrice)
                                    },
                                    0
                                  )

                                  // 自動更新結帳總金額
                                  setEditedBooking((prev) => ({
                                    ...prev,
                                    discountedPrice: newTotalPrice
                                  }))
                                }}
                                style={{ width: 120 }}
                                prefix='$'
                              />
                            </div>
                          </CompactValue>
                        </CompactInfoRow>
                      </div>
                    )
                  })}
                </ServiceCard>
              )}

              {/* 如果沒有選擇任何服務，顯示提示 */}
              {selectedMainServices.length === 0 &&
                selectedAdditionalServices.length === 0 && (
                  <div
                    style={{
                      padding: '12px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      textAlign: 'center',
                      color: '#6c757d',
                      fontSize: '14px'
                    }}
                  >
                    請點擊上方按鈕選擇服務項目
                  </div>
                )}
            </div>
          ) : (
            <div>
              {booking.services && Array.isArray(booking.services) && (
                <>
                  {booking.services.filter((s) => s.category === 'additional')
                    .length > 0 && (
                    <CompactInfoRow>
                      <CompactLabel>額外服務</CompactLabel>
                      <CompactValue>
                        <TagContainer>
                          {booking.services
                            .filter((s) => s.category === 'additional')
                            .map((service, index) => {
                              const priceToShow =
                                service.actualPrice ??
                                service.price ??
                                service.basePrice ??
                                0
                              const quantity = service.quantity || 1
                              const totalPrice = priceToShow * quantity
                              return (
                                <ServiceTag key={index}>
                                  {service.serviceName}
                                  {quantity > 1 ? ` × ${quantity}` : ''} - $
                                  {totalPrice}
                                </ServiceTag>
                              )
                            })}
                        </TagContainer>
                      </CompactValue>
                    </CompactInfoRow>
                  )}
                </>
              )}
            </div>
          )}

          <InfoGrid>
            <InfoItem>
              <InfoLabel>設計師</InfoLabel>
              <InfoValue>
                {editMode ? (
                  <Select
                    value={editedBooking.stylistId || undefined}
                    onChange={(value) =>
                      setEditedBooking({ ...editedBooking, stylistId: value })
                    }
                    placeholder='請選擇設計師'
                    style={{ width: '100%' }}
                  >
                    {stylistRoster.length === 0 ? (
                      <Option disabled value='__loading'>
                        載入當天班表中…
                      </Option>
                    ) : (
                      <>
                        {rosterGroups.shift.length > 0 && (
                          <OptGroup label={`當天有值班（${rosterGroups.shift.length}）`}>
                            {rosterGroups.shift.map((e) => (
                              <Option key={e._id} value={e._id}>
                                {describeRosterEntry(e)}
                              </Option>
                            ))}
                          </OptGroup>
                        )}
                        {rosterGroups.off.length > 0 && (
                          <OptGroup label={`當日休假（${rosterGroups.off.length}）`}>
                            {rosterGroups.off.map((e) => (
                              <Option key={e._id} value={e._id}>
                                {describeRosterEntry(e)}
                              </Option>
                            ))}
                          </OptGroup>
                        )}
                        {rosterGroups.unset.length > 0 && (
                          <OptGroup label={`未排班（${rosterGroups.unset.length}）`}>
                            {rosterGroups.unset.map((e) => (
                              <Option key={e._id} value={e._id}>
                                {describeRosterEntry(e)}
                              </Option>
                            ))}
                          </OptGroup>
                        )}
                        {rosterGroups.blocked.length > 0 && (
                          <OptGroup label='未開放此服務'>
                            {rosterGroups.blocked.map((e) => (
                              <Option key={e._id} value={e._id} disabled>
                                {describeRosterEntry(e)}・未開放此服務
                              </Option>
                            ))}
                          </OptGroup>
                        )}
                      </>
                    )}
                  </Select>
                ) : (
                  // 待指派的單 stylistId 是 'unspecified'，不能把這個英文字露給店家看
                  booking.assignmentStatus === 'pending' ||
                  booking.stylistId === 'unspecified' ||
                  !booking.stylistId
                    ? '待指派'
                    : stylists.find((s) => s._id === booking.stylistId)?.name ||
                      booking.stylistName ||
                      '待指派'
                )}
              </InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>服務時長</InfoLabel>
              <InfoValue>
                {editMode ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px'
                    }}
                  >
                    {isCustomDuration ? (
                      <InputNumber
                        value={editedBooking?.duration ?? autoDurationFromSelection}
                        onChange={(value) =>
                          setEditedBooking({
                            ...editedBooking,
                            duration: value
                          })
                        }
                        min={5}
                        max={1440}
                        step={5}
                        addonAfter='分鐘'
                        style={{ width: '100%', maxWidth: '180px' }}
                      />
                    ) : (
                      <span>{autoDurationFromSelection} 分鐘</span>
                    )}
                    <Checkbox
                      checked={isCustomDuration}
                      onChange={(e) =>
                        handleToggleCustomDuration(e.target.checked)
                      }
                    >
                      <span style={{ fontSize: '12px' }}>
                        自訂時長
                        {isCustomDuration
                          ? `（服務預設 ${autoDurationFromSelection} 分鐘，取消勾選即還原）`
                          : '（不勾選則依服務項目自動加總）'}
                      </span>
                    </Checkbox>
                  </div>
                ) : booking.duration ? (
                  <span>
                    {booking.duration} 分鐘
                    {booking.durationMode === 'manual' && (
                      <>
                        <Tag
                          color='#d5728a'
                          style={{ marginLeft: '6px', borderRadius: '10px' }}
                        >
                          自訂
                        </Tag>
                        {autoDurationFromBooking > 0 &&
                          autoDurationFromBooking !== booking.duration && (
                            <span
                              style={{ fontSize: '12px', color: '#8c8c8c' }}
                            >
                              服務預設 {autoDurationFromBooking} 分鐘
                            </span>
                          )}
                      </>
                    )}
                  </span>
                ) : (
                  '-'
                )}
              </InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>預約來源</InfoLabel>
              <InfoValue>
                {booking.bookingSource === 'business'
                  ? '店家新增'
                  : '客人線上預約'}
              </InfoValue>
            </InfoItem>
            {booking.createdAt && (
              <InfoItem>
                <InfoLabel>建立時間</InfoLabel>
                <InfoValue>
                  {formatTz(booking.createdAt, 'YYYY年MM月DD日 HH:mm')}
                </InfoValue>
              </InfoItem>
            )}
            {booking.updatedAt && (
              <InfoItem>
                <InfoLabel>更新時間</InfoLabel>
                <InfoValue>
                  {formatTz(booking.updatedAt, 'YYYY年MM月DD日 HH:mm')}
                </InfoValue>
              </InfoItem>
            )}
          </InfoGrid>
        </ModernCard>

        {/* 價格資訊（條列式顯示） */}
        <ModernCard>
          <SectionTitle>
            <CalculatorOutlined />
            價格資訊
          </SectionTitle>

          <PriceSection>
            <div>
              {(booking.services || [])
                // 視為主要服務的條件：非 additional 的都列入（包含無分類、客製分類）
                .filter((s) => s.category !== 'additional')
                .map((service, index) => {
                  // 條列顯示優先用實際成交價；若缺值再回退到原價欄位
                  const unitPrice =
                    service.actualPrice ??
                    service.basePrice ??
                    service.originalPrice ??
                    service.price ??
                    0
                  const quantity = service.quantity || 1
                  const totalPrice = unitPrice * quantity
                  return (
                    <CompactInfoRow key={`main-${index}`}>
                      <CompactLabel>服務</CompactLabel>
                      <CompactValue>
                        {service.serviceName}
                        {quantity > 1 ? ` × ${quantity}` : ''} - ${totalPrice}
                        {service.duration != null && service.duration > 0
                          ? ` (${service.duration}分鐘)`
                          : ''}
                      </CompactValue>
                    </CompactInfoRow>
                  )
                })}
              {/* 列表／舊資料僅有 productId、無 services[] 時，仍顯示主服務列，加總才看得懂 */}
              {(() => {
                const mainFromServices = (booking.services || []).filter(
                  (s) => s.category !== 'additional'
                )
                if (mainFromServices.length > 0) return null
                if (!(booking.productName || booking.productId)) return null
                const name = booking.productName || '主要服務'
                const unitPrice = Number(booking.originalPrice) || 0
                const dur = booking.duration
                return (
                  <CompactInfoRow key='main-from-product'>
                    <CompactLabel>服務</CompactLabel>
                    <CompactValue>
                      {name} - ${unitPrice}
                      {dur != null && dur > 0 ? ` (${dur}分鐘)` : ''}
                    </CompactValue>
                  </CompactInfoRow>
                )
              })()}
              {/* 加選服務：優先從 booking.services (category=additional)，若無則從 booking.additionalServices 顯示，與預約詳情一致 */}
              {(() => {
                const fromServices =
                  booking.services?.filter(
                    (s) => s.category === 'additional'
                  ) || []
                const fromAdditional =
                  fromServices.length > 0
                    ? []
                    : Array.isArray(booking.additionalServices)
                      ? booking.additionalServices.map((s) => ({
                          serviceName: s.serviceName ?? s.name,
                          basePrice:
                            s.basePrice ??
                            s.originalPrice ??
                            s.price ??
                            s.actualPrice ??
                            0,
                          price: s.price ?? s.basePrice ?? s.actualPrice ?? 0,
                          duration: s.duration ?? 0,
                          quantity: s.quantity ?? 1
                        }))
                      : []
                const additionalList =
                  fromServices.length > 0 ? fromServices : fromAdditional
                return additionalList.map((service, index) => {
                  // 條列顯示優先用實際成交價；若缺值再回退到原價欄位
                  const unitPrice =
                    service.actualPrice ??
                    service.basePrice ??
                    service.originalPrice ??
                    service.price ??
                    0
                  const quantity = service.quantity || 1
                  const totalPrice = unitPrice * quantity
                  const name = service.serviceName ?? service.name ?? '加選服務'
                  const duration = service.duration ?? 0
                  return (
                    <CompactInfoRow key={`additional-${index}`}>
                      <CompactLabel>加選服務</CompactLabel>
                      <CompactValue>
                        {name}
                        {quantity > 1 ? ` × ${quantity}` : ''} - ${totalPrice}
                        {duration ? ` (${duration}分鐘)` : ''}
                      </CompactValue>
                    </CompactInfoRow>
                  )
                })
              })()}
              <CompactInfoRow>
                <CompactLabel>原價</CompactLabel>
                <CompactValue>
                  $
                  {(() => {
                    // 若有服務變更，優先顯示 priceCalculation 的計算結果
                    if (hasServiceChanges && priceCalculation?.originalTotal) {
                      return priceCalculation.originalTotal
                    }
                    // 若有新的實際價格，重新合計：用單價 basePrice 優先，與條列顯示一致，避免 price 存成總價時原價錯誤
                    // 注意：[] 為 truthy，必須用 length，否則僅有加選時會漏算主服務而只加總加選金額
                    if (booking.services && booking.services.length > 0) {
                      const sumServices = booking.services
                        .filter((s) => s.category !== 'additional')
                        .reduce((sum, s) => {
                          const unitPrice =
                            s.basePrice ??
                            s.originalPrice ??
                            s.price ??
                            s.actualPrice ??
                            0
                          const quantity = s.quantity || 1
                          return sum + unitPrice * quantity
                        }, 0)
                      let additionalSum = booking.services
                        .filter((s) => s.category === 'additional')
                        .reduce((sum, s) => {
                          const unitPrice =
                            s.basePrice ??
                            s.originalPrice ??
                            s.price ??
                            s.actualPrice ??
                            0
                          const quantity = s.quantity || 1
                          return sum + unitPrice * quantity
                        }, 0)
                      // 若 services 中無加選項但 API 回傳了 additionalServices，用其金額加總（與預約詳情資料來源一致）
                      if (
                        additionalSum === 0 &&
                        booking.additionalServices?.length > 0
                      ) {
                        additionalSum = booking.additionalServices.reduce(
                          (sum, s) => {
                            const unitPrice =
                              s.basePrice ??
                              s.originalPrice ??
                              s.price ??
                              s.actualPrice ??
                              0
                            const quantity = s.quantity || 1
                            return sum + unitPrice * quantity
                          },
                          0
                        )
                      }
                      return sumServices + additionalSum
                    }
                    return (
                      booking.totalOriginalPrice ??
                      (Number(booking.originalPrice) || 0) +
                        (Number(booking.additionalServicesPrice) || 0)
                    )
                  })()}
                </CompactValue>
              </CompactInfoRow>
              {booking.additionalServicesPrice > 0 &&
                !editMode &&
                !booking.additionalServices?.length &&
                !booking.services?.some((s) => s.category === 'additional') && (
                  <CompactInfoRow>
                    <CompactLabel>加選服務</CompactLabel>
                    <CompactValue>
                      ${booking.additionalServicesPrice}
                    </CompactValue>
                  </CompactInfoRow>
                )}

              {priceCalculation?.appliedCoupon && (
                <CompactInfoRow>
                  <CompactLabel>優惠券</CompactLabel>
                  <CompactValue>
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                        justifyContent: 'flex-end'
                      }}
                    >
                      <Text strong style={{ color: '#d5728a' }}>
                        {priceCalculation.appliedCoupon.code}
                      </Text>
                      {priceCalculation.discountAmount > 0 && (
                        <Text style={{ color: '#e74c3c' }}>
                          -${priceCalculation.discountAmount}
                        </Text>
                      )}
                    </div>
                  </CompactValue>
                </CompactInfoRow>
              )}

              <CompactInfoRow>
                <CompactLabel>結帳總金額</CompactLabel>
                <CompactValue>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      justifyContent: 'flex-end'
                    }}
                  >
                    <Text strong>
                      $
                      {(() => {
                        let displayPrice
                        if (editMode) {
                          // 編輯模式下，優先顯示 editedBooking.discountedPrice
                          displayPrice =
                            editedBooking.discountedPrice ??
                            priceCalculation?.finalPrice ??
                            priceCalculation?.originalTotal ??
                            booking.discountedPrice ??
                            0
                        } else {
                          // 非編輯模式下，顯示 booking 的價格
                          displayPrice = booking.discountedPrice ?? 0
                        }

                        return displayPrice
                      })()}
                    </Text>
                    {editMode && (
                      <Button
                        size='small'
                        icon={<EditOutlined />}
                        onClick={() => {
                          // 保存當前價格到臨時狀態
                          setTempPrice(editedBooking?.discountedPrice || 0)
                          setShowPriceEditModal(true)
                        }}
                        style={{ borderRadius: 6 }}
                      >
                        修改
                      </Button>
                    )}
                  </div>
                </CompactValue>
              </CompactInfoRow>

              {/* 只有當店家啟用訂金功能時才顯示訂金相關信息 */}
              {depositSettings?.isEnabled && (
                <>
                  <CompactInfoRow>
                    <CompactLabel>定金狀態</CompactLabel>
                    <CompactValue>
                      {editMode ? (
                        <Select
                          value={
                            editedBooking.depositStatus ||
                            booking.depositStatus ||
                            'not_required'
                          }
                          onChange={(value) =>
                            setEditedBooking({
                              ...editedBooking,
                              depositStatus: value
                            })
                          }
                          style={{ width: '160px' }}
                        >
                          <Option value='paid'>已付款</Option>
                          <Option value='unpaid'>未付款</Option>
                          <Option value='not_required'>免訂金</Option>
                        </Select>
                      ) : (
                        <StatusBadge
                          {...getDepositStatusConfig(booking.depositStatus)}
                        >
                          {getDepositStatusConfig(booking.depositStatus).text}
                        </StatusBadge>
                      )}
                    </CompactValue>
                  </CompactInfoRow>

                  {/* 定金收款方式。線上刷卡是系統自動寫的，這裡只處理
                      店家自己收款後回來標記的情形 */}
                  {(() => {
                    const status =
                      editedBooking.depositStatus ||
                      booking.depositStatus ||
                      'not_required'
                    if (status !== 'paid') return null
                    if (!editMode) {
                      const m = booking.depositPayment?.method
                      if (!m) return null
                      const label =
                        m === 'credit'
                          ? booking.depositPayment?.source === 'online'
                            ? '線上刷卡'
                            : '櫃檯刷卡'
                          : m === 'cash'
                          ? '現金'
                          : '匯款'
                      return (
                        <CompactInfoRow>
                          <CompactLabel>定金收款方式</CompactLabel>
                          <CompactValue>
                            {label}
                            {booking.depositPayment?.fee > 0 &&
                              `（手續費 $${booking.depositPayment.fee}）`}
                          </CompactValue>
                        </CompactInfoRow>
                      )
                    }
                    return (
                      <CompactInfoRow>
                        <CompactLabel>定金收款方式</CompactLabel>
                        <CompactValue>
                          <Select
                            value={
                              editedBooking.depositPaymentMethod ||
                              booking.depositPayment?.method ||
                              'bank_transfer'
                            }
                            onChange={(value) =>
                              setEditedBooking({
                                ...editedBooking,
                                depositPaymentMethod: value
                              })
                            }
                            style={{ width: '160px' }}
                          >
                            <Option value='bank_transfer'>匯款</Option>
                            <Option value='cash'>現金</Option>
                            {depositSettings?.allowCounterCardPayment && (
                              <Option value='credit'>櫃檯刷卡</Option>
                            )}
                          </Select>
                        </CompactValue>
                      </CompactInfoRow>
                    )
                  })()}

                  {/* 定金信息 */}
                  {booking.depositAmount !== undefined &&
                    booking.depositAmount !== null && (
                      <CompactInfoRow>
                        <CompactLabel>定金金額</CompactLabel>
                        <CompactValue>
                          ${booking.depositAmount?.toLocaleString() || 0}
                        </CompactValue>
                      </CompactInfoRow>
                    )}

                  {booking.depositBankNumber && (
                    <CompactInfoRow>
                      <CompactLabel>銀行帳號</CompactLabel>
                      <CompactValue>{booking.depositBankNumber}</CompactValue>
                    </CompactInfoRow>
                  )}

                  {booking.depositDueDate && (
                    <CompactInfoRow>
                      <CompactLabel>定金付款期限</CompactLabel>
                      <CompactValue>
                        {formatTz(
                          booking.depositDueDate,
                          'YYYY年MM月DD日 HH:mm'
                        )}
                      </CompactValue>
                    </CompactInfoRow>
                  )}
                </>
              )}

              {!booking.hasCheckedOut && (
                <div style={{ marginTop: 8, textAlign: 'right' }}>
                  <Button
                    type='link'
                    icon={<CalculatorOutlined />}
                    onClick={() => setShowPricePreview(true)}
                    loading={isCalculatingPrice}
                  >
                    價格明細
                  </Button>
                </div>
              )}
            </div>

            {booking.appliedCoupon && (
              <div
                style={{
                  marginTop: '16px',
                  padding: '16px',
                  background: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #e5e5e7'
                }}
              >
                <InfoLabel>使用優惠券</InfoLabel>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Text strong>{booking.appliedCoupon.code}</Text>
                  {booking.appliedCoupon.discount > 0 && (
                    <Text style={{ color: '#34c759' }}>
                      -${booking.appliedCoupon.discount}
                    </Text>
                  )}
                </div>
              </div>
            )}
          </PriceSection>
        </ModernCard>

        {/* 備註 */}
        <ModernCard>
          <SectionTitle>
            <InfoCircleOutlined />
            備註
          </SectionTitle>
          {editMode ? (
            <Input.TextArea
              value={editedBooking.note || ''}
              onChange={(e) =>
                setEditedBooking({ ...editedBooking, note: e.target.value })
              }
              placeholder='請輸入備註...'
              rows={4}
              style={{ border: '1px solid #e5e5e7', borderRadius: '8px' }}
            />
          ) : (
            <div
              style={{
                padding: '16px',
                background: '#f5f5f7',
                borderRadius: '8px',
                border: '1px solid #e5e5e7',
                minHeight: '60px'
              }}
            >
              {booking.note || '無備註'}
            </div>
          )}
        </ModernCard>

        {/* 施作照片（高階方案限定，未開通或無權限時自動隱藏） */}
        <ServicePhotoBookingSection
          businessId={businessId}
          bookingId={booking._id}
        />
      </ContentSection>

      {/* 服務選擇器 */}
      <ServiceSelector
        visible={showServiceSelector}
        onClose={() => {
          console.log('ServiceSelector 關閉')
          setShowServiceSelector(false)
        }}
        onServicesSelected={async (serviceData) => {
          const {
            services,
            selectedServiceIds,
            suggestedTotal,
            priceCalculation: servicePriceCalculation
          } = serviceData

          console.log('=== ServiceSelector 返回的服務數據 ===')
          console.log('serviceData:', serviceData)
          console.log('services:', services)

          const mainServicesFromSelector = services.filter(
            (s) => s.category === 'main'
          )
          const additionalServicesFromSelector = services.filter(
            (s) => s.category === 'additional'
          )

          console.log('mainServicesFromSelector:', mainServicesFromSelector)
          console.log(
            'additionalServicesFromSelector:',
            additionalServicesFromSelector
          )

          // 從完整的產品列表中查找主要服務的完整對象
          const fullMainServices = mainServicesFromSelector
            .map((s) => {
              const serviceId = s.serviceId || s._id
              const foundProduct = products.find(
                (p) => p._id === serviceId || p.id === serviceId
              )
              console.log(`查找主要服務 ${serviceId}:`, foundProduct)
              return foundProduct || null
            })
            .filter(Boolean)

          // 從完整的額外服務列表中查找額外服務的完整對象
          const fullAdditionalServices = additionalServicesFromSelector
            .map((s) => {
              const serviceId = s.serviceId || s._id
              const found = additionalServices.find(
                (as) =>
                  as._id === serviceId ||
                  as.id === serviceId ||
                  as.serviceId === serviceId
              )
              console.log(`查找額外服務 ${serviceId}:`, found)
              return found || null
            })
            .filter(Boolean)

          console.log('fullMainServices 最終結果:', fullMainServices)
          console.log(
            'fullAdditionalServices 最終結果:',
            fullAdditionalServices
          )

          // 設定主要服務多選，並保留第一個作為舊欄位 fallback
          setSelectedMainServices(fullMainServices)
          setSelectedProduct(
            fullMainServices.length > 0 ? fullMainServices[0] : null
          )
          setSelectedAdditionalServices(fullAdditionalServices)
          setHasServiceChanges(true) // 標記有服務變更

          // 直接使用 ServiceSelector 傳遞的價格數據
          if (servicePriceCalculation) {
            setPriceCalculation(servicePriceCalculation)
            setEditedBooking({
              ...editedBooking,
              discountedPrice: servicePriceCalculation.finalPrice,
              originalPrice:
                servicePriceCalculation.originalTotal ??
                editedBooking.originalPrice
            })
          } else if (suggestedTotal) {
            setEditedBooking({
              ...editedBooking,
              discountedPrice: suggestedTotal
            })
          }

          setShowServiceSelector(false)
        }}
        currentServices={currentServicesForSelector}
        bookingId={booking?._id}
        onPriceCalculation={(priceCalculation) => {
          setPriceCalculation(priceCalculation)
          // 如果在編輯模式且有價格計算結果，更新編輯中的預約價格
          if (editMode && priceCalculation?.finalPrice) {
            setEditedBooking({
              ...editedBooking,
              discountedPrice: priceCalculation.finalPrice,
              originalPrice:
                priceCalculation.originalTotal ?? editedBooking.originalPrice
            })
          }
        }}
      />

      {/* 價格預覽 Modal */}
      <Modal
        title='價格計算結果'
        open={showPricePreview}
        onCancel={() => setShowPricePreview(false)}
        footer={null}
      >
        {priceCalculation && (
          <div style={{ width: '100%' }}>
            {/* 服務明細 */}
            {priceCalculation.serviceDetails &&
              priceCalculation.serviceDetails.length > 0 && (
                <div
                  style={{
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    marginBottom: '12px',
                    border: '1px solid #e5e5e7'
                  }}
                >
                  <div
                    style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#1d1d1f',
                      marginBottom: '10px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <div
                      style={{
                        width: '3px',
                        height: '16px',
                        background: '#d5728a',
                        borderRadius: '2px',
                        marginRight: '8px'
                      }}
                    />
                    服務明細
                  </div>
                  {priceCalculation.serviceDetails.map((service, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 0',
                        borderBottom:
                          index < priceCalculation.serviceDetails.length - 1
                            ? '1px solid #e5e5e7'
                            : 'none'
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: '15px',
                            color: '#1d1d1f',
                            fontWeight: '500'
                          }}
                        >
                          {service.serviceName}
                        </div>
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#86868b',
                            marginTop: '1px'
                          }}
                        >
                          {service.duration} 分鐘
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#1d1d1f'
                        }}
                      >
                        ${service.price}
                      </div>
                    </div>
                  ))}
                </div>
              )}

            {/* 加選服務明細 */}
            {priceCalculation.additionalServicesDetails &&
              priceCalculation.additionalServicesDetails.length > 0 && (
                <div
                  style={{
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    marginBottom: '12px',
                    border: '1px solid #e5e5e7'
                  }}
                >
                  <div
                    style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#1d1d1f',
                      marginBottom: '10px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <div
                      style={{
                        width: '3px',
                        height: '16px',
                        background: '#34c759',
                        borderRadius: '2px',
                        marginRight: '8px'
                      }}
                    />
                    加選服務明細
                  </div>
                  {priceCalculation.additionalServicesDetails.map(
                    (service, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 0',
                          borderBottom:
                            index <
                            priceCalculation.additionalServicesDetails.length -
                              1
                              ? '1px solid #e5e5e7'
                              : 'none'
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: '15px',
                              color: '#1d1d1f',
                              fontWeight: '500'
                            }}
                          >
                            + {service.serviceName}
                          </div>
                          <div
                            style={{
                              fontSize: '12px',
                              color: '#86868b',
                              marginTop: '1px'
                            }}
                          >
                            {service.duration} 分鐘
                          </div>
                        </div>
                        <div
                          style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#1d1d1f'
                          }}
                        >
                          ${service.price}
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}

            {/* 價格總結 */}
            <div
              style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid #e5e5e7'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: '1px solid #e5e5e7'
                }}
              >
                <span style={{ fontSize: '15px', color: '#86868b' }}>
                  原始總價
                </span>
                <span
                  style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#1d1d1f'
                  }}
                >
                  ${priceCalculation.originalTotal}
                </span>
              </div>

              {priceCalculation.discountAmount > 0 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px solid #e5e5e7'
                  }}
                >
                  <span style={{ fontSize: '15px', color: '#86868b' }}>
                    折扣金額
                  </span>
                  <span
                    style={{
                      fontSize: '16px',
                      fontWeight: '500',
                      color: '#ff3b30'
                    }}
                  >
                    -${priceCalculation.discountAmount}
                  </span>
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 0 0 0',
                  marginTop: '8px'
                }}
              >
                <span
                  style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1d1d1f'
                  }}
                >
                  最終價格
                </span>
                <span
                  style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#d5728a'
                  }}
                >
                  ${priceCalculation.finalPrice}
                </span>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* 價格修改 Modal */}
      <Modal
        title='修改結帳總金額'
        open={showPriceEditModal}
        onCancel={() => {
          // 取消時恢復原價格
          if (tempPrice !== null) {
            setEditedBooking({
              ...editedBooking,
              discountedPrice: tempPrice
            })
          }
          setShowPriceEditModal(false)
          setTempPrice(null)
        }}
        footer={[
          <Button
            key='cancel'
            onClick={() => {
              // 取消時恢復原價格
              if (tempPrice !== null) {
                setEditedBooking({
                  ...editedBooking,
                  discountedPrice: tempPrice
                })
              }
              setShowPriceEditModal(false)
              setTempPrice(null)
            }}
          >
            取消
          </Button>,
          <Button
            key='save'
            type='primary'
            onClick={() => {
              // 確認修改價格
              const price = editedBooking?.discountedPrice

              // 允許 0，只拒絕負數、undefined、null 或非數字
              if (
                price !== undefined &&
                price !== null &&
                typeof price === 'number' &&
                price >= 0
              ) {
                setHasServiceChanges(true) // 標記有變更，讓頁面顯示 editedBooking.discountedPrice
                setShowPriceEditModal(false)
                setTempPrice(null)

                message.success('請務必點選保存完成修改')
              } else {
                // 根據不同情況顯示不同的錯誤訊息
                if (price === undefined || price === null) {
                  message.error('請輸入有效的價格')
                } else if (typeof price !== 'number') {
                  message.error('價格必須為數字')
                } else if (price < 0) {
                  message.error('價格不能為負數')
                } else {
                  message.error('請輸入有效的價格')
                }
              }
            }}
          >
            確認修改
          </Button>
        ]}
      >
        <Space direction='vertical' style={{ width: '100%' }}>
          {/* 系統建議價格 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e5e5e7'
            }}
          >
            <Text style={{ color: '#6c757d', fontSize: '14px' }}>
              系統建議價格
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Text strong style={{ color: '#d5728a', fontSize: '16px' }}>
                $
                {priceCalculation?.finalPrice ||
                  priceCalculation?.originalTotal ||
                  0}
              </Text>
              <Button
                size='small'
                style={{
                  background: '#d5728a',
                  borderColor: '#d5728a',
                  color: '#fff',
                  borderRadius: '6px'
                }}
                onClick={() => {
                  setEditedBooking({
                    ...editedBooking,
                    discountedPrice:
                      priceCalculation?.finalPrice ||
                      priceCalculation?.originalTotal ||
                      0
                  })
                  setShowPriceEditModal(false)
                }}
              >
                採用
              </Button>
            </div>
          </div>

          {/* 結帳總金額輸入 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
              background: '#fff',
              borderRadius: '8px',
              border: '1px solid #e5e5e7'
            }}
          >
            <Text style={{ color: '#6c757d', fontSize: '14px' }}>
              結帳總金額
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <InputNumber
                value={editedBooking?.discountedPrice ?? 0}
                onChange={(value) => {
                  // 如果 value 是 null 或 undefined，設為 0
                  const newValue =
                    value === null || value === undefined ? 0 : value
                  setEditedBooking((prev) => ({
                    ...(prev || {}),
                    discountedPrice: newValue
                  }))
                }}
                style={{ width: '120px' }}
                formatter={(value) =>
                  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                min={0}
                precision={0}
              />
            </div>
          </div>
        </Space>
      </Modal>
    </PageContainer>
  )
}

export default BookingDetailPage
